import React, { useState, useEffect, useRef } from "react";
import { COLORS } from "../../theme/colors";
import { MATERIALS, RADIUS } from "../../theme/materials";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { analyticsApi } from "../../api/client";
import { Button, Badge, Input } from "../../components/ui";
import PageHeader from "../../components/PageHeader";
import { MarkdownText } from "../../components/MarkdownText";
import ABTestPanel from "./ABTestPanel";
import { inspectorConversationPath } from "./inspectorConversationPath";

const WS_BASE = (window.location.protocol === "https:" ? "wss://" : "ws://") + window.location.host;

const MiniBarChart = ({ data, maxVal, color }) => (
  <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 48 }}>
    {data.map((v, i) => (
      <div key={i} style={{
        flex: 1, height: `${(v / maxVal) * 100}%`, borderRadius: 3,
        background: `linear-gradient(180deg, ${color}, ${color}44)`, minWidth: 6,
        transition: "height 0.3s",
      }} />
    ))}
  </div>
);

import { useNavigate } from "react-router-dom";

// B5: deep-link into the full-page Conversation Inspector from ES message
// results. Style mirrors the Supervisor row's "Open in Inspector" affordance
// (transparent bg, bordered pill, accent text) so the two conversation-list
// surfaces feel identical. Hidden when the hit carries no conversation id
// (guard lives in inspectorConversationPath).
const OpenInInspectorLink = ({ conversationId, navigate }) => {
  const path = inspectorConversationPath(conversationId);
  if (!path) return null;
  return (
    <button
      type="button"
      title="Open in Inspector (debug spans / replay)"
      aria-label="Open in Inspector"
      data-testid="es-message-inspector-link"
      onClick={(e) => {
        e.stopPropagation();
        navigate(path);
      }}
      style={{
        background: "transparent",
        border: `1px solid ${COLORS.border}`,
        borderRadius: 4,
        color: COLORS.accent,
        cursor: "pointer",
        fontSize: 10, fontWeight: 600,
        padding: "3px 8px",
        fontFamily: "inherit",
        whiteSpace: "nowrap",
        marginLeft: "auto",
      }}
    >
      Open in Inspector ↗
    </button>
  );
};

const AnalyticsView = () => {
  const bp = useBreakpoint();
  const _navigate = useNavigate();

  // Dashboard state
  const [dashData, setDashData] = useState(null);
  const [dashLoading, setDashLoading] = useState(true);
  const [dashPeriod, setDashPeriod] = useState(7);

  // Ask AI state
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiHistory, setAiHistory] = useState([]); // [{question, answer, sql, rows, columns, row_count}]
  const [aiAsking, setAiAsking] = useState(false);

  // Elasticsearch Explorer state
  const [esIndex, setEsIndex] = useState("messages");
  const [esQuery, setEsQuery] = useState("");
  const [esResults, setEsResults] = useState(null);
  const [esSearching, setEsSearching] = useState(false);
  const [esStats, setEsStats] = useState(null);
  const [esExpandedId, setEsExpandedId] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiSummarizing, setAiSummarizing] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [datePreset, setDatePreset] = useState("");

  // Fetch dashboard data
  useEffect(() => {
    const loadDashboard = async () => {
      setDashLoading(true);
      try {
        const res = await analyticsApi.dashboard(dashPeriod);
        if (res.success) setDashData(res.data);
      } catch {}
      setDashLoading(false);
    };
    loadDashboard();
  }, [dashPeriod]);

  useEffect(() => {
    const loadEsStats = async () => {
      try {
        const res = await analyticsApi.esStats();
        if (res.success) setEsStats(res.data);
      } catch {}
    };
    loadEsStats();
  }, []);

  const buildDateFilters = () => {
    const f = {};
    if (dateFrom) f.date_from = new Date(dateFrom).toISOString();
    if (dateTo) f.date_to = new Date(dateTo + "T23:59:59").toISOString();
    return Object.keys(f).length ? f : null;
  };

  const applyDatePreset = (preset) => {
    setDatePreset(preset);
    if (!preset) { setDateFrom(""); setDateTo(""); return; }
    const now = new Date();
    const to = now.toISOString().split("T")[0];
    const from = new Date(now);
    if (preset === "24h") from.setDate(from.getDate() - 1);
    else if (preset === "7d") from.setDate(from.getDate() - 7);
    else if (preset === "30d") from.setDate(from.getDate() - 30);
    else if (preset === "90d") from.setDate(from.getDate() - 90);
    setDateFrom(from.toISOString().split("T")[0]);
    setDateTo(to);
  };

  const askWsRef = useRef(null);

  const handleAskAI = () => {
    const q = aiQuestion.trim();
    if (!q || aiAsking) return;

    // Close any previous connection
    if (askWsRef.current) {
      askWsRef.current.close();
      askWsRef.current = null;
    }

    setAiAsking(true);
    setAiQuestion("");
    // Add placeholder entry — answer streams in
    setAiHistory(prev => [...prev, { question: q, answer: "", sql: null, rows: null, columns: null, row_count: null, loading: true, streaming: true }]);

    // F (#16): the i2_access cookie authenticates the WS handshake (same origin).
    const ws = new WebSocket(`${WS_BASE}/ws/analytics/ask`);
    askWsRef.current = ws;

    // Token queue + drain interval — same pattern as chat streaming
    const tokenQueue = [];
    let streamDone = false;
    const interval = setInterval(() => {
      if (tokenQueue.length > 0) {
        const token = tokenQueue.shift();
        setAiHistory(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { ...updated[updated.length - 1], answer: (updated[updated.length - 1].answer || "") + token };
          return updated;
        });
      } else if (streamDone) {
        clearInterval(interval);
        setAiHistory(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { ...updated[updated.length - 1], loading: false, streaming: false };
          return updated;
        });
        setAiAsking(false);
        askWsRef.current = null;
      }
    }, 28);

    ws.onopen = () => {
      ws.send(JSON.stringify({ question: q }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "sql") {
          setAiHistory(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { ...updated[updated.length - 1], sql: data.sql };
            return updated;
          });
        } else if (data.type === "rows") {
          setAiHistory(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { ...updated[updated.length - 1], rows: data.rows, columns: data.columns, row_count: data.row_count };
            return updated;
          });
        } else if (data.type === "token") {
          tokenQueue.push(data.token);
        } else if (data.type === "done") {
          streamDone = true;
          ws.close();
        } else if (data.type === "error") {
          clearInterval(interval);
          setAiHistory(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { ...updated[updated.length - 1], answer: data.message || "Error", error: true, loading: false, streaming: false };
            return updated;
          });
          setAiAsking(false);
          askWsRef.current = null;
        }
      } catch {}
    };

    ws.onerror = () => {
      clearInterval(interval);
      setAiHistory(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { ...updated[updated.length - 1], answer: "Connection error", error: true, loading: false, streaming: false };
        return updated;
      });
      setAiAsking(false);
      askWsRef.current = null;
    };

    ws.onclose = () => {
      // If closed before done signal (e.g. network drop), finalize
      if (!streamDone) {
        streamDone = true;
      }
    };
  };

  const handleEsSearch = async (overrideQuery, overrideIndex) => {
    const q = overrideQuery ?? esQuery;
    const idx = overrideIndex ?? esIndex;
    setEsSearching(true);
    setEsResults(null);
    setEsExpandedId(null);
    try {
      const res = await analyticsApi.esSearch(idx, q, buildDateFilters(), 20);
      if (res.success) setEsResults({ ...res.data, index: idx });
    } catch (err) {
      setEsResults({ hits: [], total: 0, took_ms: 0, error: err.message || "Search failed", index: idx });
    } finally {
      setEsSearching(false);
    }
  };

  const handleAiSummarize = async () => {
    setAiSummarizing(true);
    setAiSummary(null);
    try {
      const res = await analyticsApi.esSummarize(esQuery, buildDateFilters(), 50);
      if (res.success) setAiSummary(res.data);
    } catch (err) {
      setAiSummary({ summary: err.message || "Summarization failed", message_count: 0, error: true });
    } finally {
      setAiSummarizing(false);
    }
  };

  const quickFilters = [
    { label: "All Messages", index: "messages", query: "" },
    { label: "Recent Uploads", index: "files", query: "" },
    { label: "Platform Events", index: "events", query: "" },
    { label: "Chat Channel", index: "messages", query: "chat" },
    { label: "PDF Files", index: "files", query: "pdf" },
    { label: "File Uploads", index: "events", query: "file.uploaded" },
  ];

  const formatBytes = (bytes) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  return (
    <div className="panel-scroll" style={{ flex: 1, padding: bp.isMobile ? "16px 12px 80px" : "24px 28px", overflowY: "auto" }}>
      <PageHeader
        title="Analytics"
        subtitle="Real-time insights across all channels"
        filters={
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {/* Phase 1.5 #2 · entry point for Datasets/Prompts/Evals/Annotations */}
            <button
              onClick={() => _navigate("/analytics/observability/datasets")}
              style={{
                padding: "6px 12px", fontSize: 11, fontWeight: 600, borderRadius: 5,
                background: "transparent",
                color: COLORS.accentText || COLORS.accent || "#2563eb",
                border: `1px solid ${COLORS.border}`, cursor: "pointer",
                marginRight: 8,
              }}
              title="Datasets / Prompts / Evals / Annotations"
            >
              ⚙ Observability
            </button>
            {[{ label: "7D", days: 7 }, { label: "30D", days: 30 }, { label: "90D", days: 90 }].map(p => (
              <Button key={p.days} variant={dashPeriod === p.days ? "primary" : "secondary"} size="sm"
                onClick={() => setDashPeriod(p.days)}
                style={dashPeriod === p.days
                  ? { background: COLORS.accentSoft, color: COLORS.accentText, border: `1px solid ${COLORS.accent}`, padding: "6px 14px", fontSize: 12 }
                  : { padding: "6px 14px", fontSize: 12 }
                }>
                {p.label}
              </Button>
            ))}
          </div>
        }
      />

      {/* KPI cards */}
      <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)", gap: bp.isMobile ? 8 : 14, marginBottom: 24 }}>
        {dashLoading ? (
          [1,2,3,4,5,6].map(i => (
            <div key={i} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, padding: "18px 20px", boxShadow: MATERIALS.spatialShadow(), height: 100 }}>
              <div style={{ fontSize: 11, color: COLORS.textDim }}>Loading...</div>
            </div>
          ))
        ) : (dashData?.kpis || []).map(kpi => (
          <div key={kpi.label} style={{
            background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
            padding: "18px 20px",
          }}>
            <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 500, marginBottom: 8 }}>{kpi.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: COLORS.text, letterSpacing: "-0.03em" }}>{kpi.value}</div>
            <div style={{
              fontSize: 12, fontWeight: 600, marginTop: 6,
              color: kpi.up ? COLORS.green : COLORS.pink,
            }}>{kpi.change}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1fr" : "2fr 1fr", gap: 14, marginBottom: 24 }}>
        {/* Stacked bar chart */}
        <div style={{
          background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
          padding: "20px 24px",
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>Conversations by Channel</div>
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 20 }}>Last {dashPeriod} days</div>
          {(dashData?.conversations || []).length > 0 ? (
            <>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 12, height: 160 }}>
                {(dashData?.conversations || []).map((d, i) => {
                  const total = (d.voice || 0) + (d.whatsapp || 0) + (d.chat || 0);
                  if (total === 0) return (
                    <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                      <div style={{ width: "100%", height: 140, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
                        <div style={{ width: "60%", height: 4, borderRadius: 6, background: COLORS.border }} />
                      </div>
                      <div style={{ fontSize: 10, color: COLORS.textDim }}>{d.day}</div>
                    </div>
                  );
                  return (
                    <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                      <div style={{
                        width: "100%", height: 140, display: "flex", flexDirection: "column-reverse",
                        borderRadius: 6, overflow: "hidden", gap: 1,
                      }}>
                        {d.voice > 0 && <div style={{ height: `${(d.voice / total) * 100}%`, background: COLORS.orange, borderRadius: "0 0 6px 6px", minHeight: 4 }} />}
                        {d.chat > 0 && <div style={{ height: `${(d.chat / total) * 100}%`, background: COLORS.accent, minHeight: 4 }} />}
                        {d.whatsapp > 0 && <div style={{ height: `${(d.whatsapp / total) * 100}%`, background: COLORS.green, borderRadius: "6px 6px 0 0", minHeight: 4 }} />}
                      </div>
                      <div style={{ fontSize: 10, color: COLORS.textDim }}>{d.day}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: 16, marginTop: 16, justifyContent: "center" }}>
                {[
                  { label: "WhatsApp", color: COLORS.green },
                  { label: "Chat", color: COLORS.accentText },
                  { label: "Voice", color: COLORS.orange },
                ].map(l => (
                  <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: l.color }} />
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>{l.label}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 160, color: COLORS.textDim, fontSize: 13 }}>
              {dashLoading ? "Loading..." : "No conversation data yet"}
            </div>
          )}
        </div>

        {/* Channel breakdown */}
        <div style={{
          background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
          padding: "20px 24px",
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>Channel Split</div>
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 20 }}>Distribution — last {dashPeriod} days</div>
          {(dashData?.channels || []).length > 0 ? (
            (dashData?.channels || []).map(ch => (
              <div key={ch.name} style={{ marginBottom: 18 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 500, color: COLORS.text }}>{ch.name}</span>
                  <span style={{ fontSize: 12, color: COLORS.textMuted }}>{(ch.count || 0).toLocaleString()} ({ch.pct}%)</span>
                </div>
                <div style={{ height: 6, borderRadius: 3, background: COLORS.surface, overflow: "hidden" }}>
                  <div style={{
                    height: "100%", width: `${ch.pct}%`, borderRadius: 3,
                    background: `linear-gradient(90deg, ${ch.color || COLORS.accent}, ${(ch.color || COLORS.accent)}88)`,
                    boxShadow: `0 0 8px ${(ch.color || COLORS.accent)}44`,
                  }} />
                </div>
              </div>
            ))
          ) : (
            <div style={{ color: COLORS.textDim, fontSize: 12, padding: "20px 0" }}>{dashLoading ? "Loading..." : "No data yet"}</div>
          )}

          {dashData?.kpis?.[1] && (
            <div style={{
              marginTop: 20, padding: "14px", borderRadius: 10,
              background: `${COLORS.green}08`, border: `1px solid ${COLORS.green}20`,
            }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.green }}>{dashData.kpis[1].value}</div>
              <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>AI Resolution Rate</div>
            </div>
          )}
        </div>
      </div>

      {/* Agent performance table */}
      <div style={{
        background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
        className: "table-scroll", padding: bp.isMobile ? "16px 12px" : "20px 24px", overflowX: "auto",
        marginBottom: 24,
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 16 }}>Agent Performance</div>
        {(dashData?.agents || []).length > 0 ? (
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1.5fr 1fr 1fr", gap: 0, minWidth: bp.isMobile ? 600 : "auto" }}>
            {["Agent", "Status", "Conversations", "Resolution", "Channels"].map(h => (
              <div key={h} style={{
                fontSize: 10, fontWeight: 700, color: COLORS.textDim, letterSpacing: "0.06em",
                textTransform: "uppercase", padding: "0 0 12px 0", borderBottom: `1px solid ${COLORS.border}`,
              }}>{h}</div>
            ))}
            {(dashData?.agents || []).map((a, idx) => (
              <React.Fragment key={idx}>
                <div style={{ padding: "14px 0", fontSize: 13, fontWeight: 500, color: COLORS.text, borderBottom: `1px solid ${COLORS.border}` }}>{a.name}</div>
                <div style={{ padding: "14px 0", borderBottom: `1px solid ${COLORS.border}` }}>
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: "3px 8px", borderRadius: 6,
                    background: a.status === "live" ? `${COLORS.green}15` : `${COLORS.orange}15`,
                    color: a.status === "live" ? COLORS.green : COLORS.orange,
                  }}>{a.status}</span>
                </div>
                <div style={{ padding: "14px 0", display: "flex", alignItems: "center", gap: 10, borderBottom: `1px solid ${COLORS.border}` }}>
                  <span style={{ fontSize: 13, color: COLORS.text, fontWeight: 500 }}>{(a.conversations || 0).toLocaleString()}</span>
                </div>
                <div style={{ padding: "14px 0", fontSize: 13, color: (a.resolution || 0) >= 90 ? COLORS.green : COLORS.orange, fontWeight: 600, borderBottom: `1px solid ${COLORS.border}` }}>{a.resolution || 0}%</div>
                <div style={{ padding: "14px 0", display: "flex", gap: 4, borderBottom: `1px solid ${COLORS.border}` }}>
                  {(a.channels || []).map(c => (
                    <span key={c} style={{ fontSize: 14 }}>{c === "whatsapp" ? "\uD83D\uDCAC" : c === "voice" ? "\uD83D\uDCDE" : "\uD83C\uDF10"}</span>
                  ))}
                </div>
              </React.Fragment>
            ))}
          </div>
        ) : (
          <div style={{ color: COLORS.textDim, fontSize: 12, padding: "20px 0", textAlign: "center" }}>
            {dashLoading ? "Loading agent performance..." : "No agent activity recorded yet. Start chatting with your agents to see data here."}
          </div>
        )}
      </div>

      {/* API Calls by Provider */}
      <div style={{
        background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
        padding: bp.isMobile ? "16px 12px" : "20px 24px", overflowX: "auto",
        marginBottom: 24,
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 16 }}>External API Calls by Provider</div>
        {(dashData?.api_calls_by_provider || []).length > 0 ? (
          <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1.5fr 1.5fr 1fr 1fr 1fr 1fr", gap: 0, minWidth: bp.isMobile ? 700 : "auto" }}>
            {["Provider", "Service", "Model", "Calls", "Errors", "Avg Latency", "Tokens"].map(h => (
              <div key={h} style={{
                fontSize: 10, fontWeight: 700, color: COLORS.textDim, letterSpacing: "0.06em",
                textTransform: "uppercase", padding: "0 0 12px 0", borderBottom: `1px solid ${COLORS.border}`,
              }}>{h}</div>
            ))}
            {(dashData?.api_calls_by_provider || []).map((row, idx) => {
              // Legacy 'milvus' key retained so historical api_call_logs rows still render with a color.
              const providerColors = { openai: COLORS.green, pgvector: COLORS.cyan, qdrant: COLORS.cyan, milvus: COLORS.cyan, mcp: COLORS.orange, deepgram: COLORS.accent, elevenlabs: COLORS.pink };
              const pColor = providerColors[row.provider] || COLORS.textMuted;
              return (
                <React.Fragment key={idx}>
                  <div style={{ padding: "12px 0", borderBottom: `1px solid ${COLORS.border}` }}>
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 6,
                      background: `${pColor}15`, color: pColor,
                    }}>{row.provider}</span>
                  </div>
                  <div style={{ padding: "12px 0", fontSize: 12, color: COLORS.text, borderBottom: `1px solid ${COLORS.border}` }}>{row.service}</div>
                  <div style={{ padding: "12px 0", fontSize: 11, color: COLORS.textMuted, fontFamily: "monospace", borderBottom: `1px solid ${COLORS.border}` }}>{row.model || "\u2014"}</div>
                  <div style={{ padding: "12px 0", fontSize: 13, fontWeight: 600, color: COLORS.text, borderBottom: `1px solid ${COLORS.border}` }}>{(row.count || 0).toLocaleString()}</div>
                  <div style={{ padding: "12px 0", fontSize: 13, fontWeight: 600, color: row.errors > 0 ? COLORS.pink : COLORS.textDim, borderBottom: `1px solid ${COLORS.border}` }}>{row.errors || 0}</div>
                  <div style={{ padding: "12px 0", fontSize: 12, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.border}` }}>{row.avg_latency_ms != null ? `${row.avg_latency_ms}ms` : "\u2014"}</div>
                  <div style={{ padding: "12px 0", fontSize: 12, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.border}` }}>{row.total_tokens ? row.total_tokens.toLocaleString() : "\u2014"}</div>
                </React.Fragment>
              );
            })}
          </div>
        ) : (
          <div style={{ color: COLORS.textDim, fontSize: 12, padding: "20px 0", textAlign: "center" }}>
            {dashLoading ? "Loading API call data..." : "No API calls recorded yet. Interact with your agents to see provider-level usage."}
          </div>
        )}
      </div>

      {/* Ask AI — Conversation Analytics */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: bp.isMobile ? 18 : 20, fontWeight: 700, color: COLORS.text, letterSpacing: "-0.02em" }}>Ask AI</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>Ask questions about your conversation data in natural language</div>
        </div>

        <div style={{
          background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12,
          padding: 16, marginBottom: 12,
        }}>
          {/* Conversation history */}
          {aiHistory.length > 0 && (
            <div style={{ maxHeight: 400, overflowY: "auto", marginBottom: 16 }}>
              {aiHistory.map((item, i) => (
                <div key={i} style={{ marginBottom: 16 }}>
                  {/* Question */}
                  <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                      background: `${COLORS.accent}15`, border: `1px solid ${COLORS.accent}30`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 13, color: COLORS.accentText,
                    }}>Q</div>
                    <div style={{ fontSize: 13, color: COLORS.text, fontWeight: 500, paddingTop: 4 }}>{item.question}</div>
                  </div>
                  {/* Answer */}
                  <div style={{ display: "flex", gap: 10 }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                      background: item.error ? `${COLORS.pink}15` : `${COLORS.green}15`,
                      border: `1px solid ${item.error ? COLORS.pink : COLORS.green}30`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 13, color: item.error ? COLORS.pink : COLORS.green,
                    }}>{item.loading ? "..." : "A"}</div>
                    <div style={{ flex: 1 }}>
                      {item.loading && !item.answer && !item.rows ? (
                        <div style={{ fontSize: 12, color: COLORS.textMuted, paddingTop: 4 }}>Generating SQL query...</div>
                      ) : (
                        <>
                          {/* Markdown renders progressively as tokens stream
                              (MarkdownText re-tokenizes via useMemo keyed on
                              text), matching the Claude-Code-style "transform
                              as it appears" behavior. Errors stay as plain
                              text so the user sees the raw exception. */}
                          <div style={{
                            fontSize: 13, color: item.error ? COLORS.pink : COLORS.text,
                            lineHeight: 1.6, paddingTop: 4,
                          }}>
                            {item.error ? (
                              <span style={{ whiteSpace: "pre-wrap" }}>{item.answer}</span>
                            ) : (
                              <MarkdownText text={item.answer || ""} />
                            )}
                            {item.streaming && <span style={{ display: "inline-block", width: 2, height: "1em", background: COLORS.accent, marginLeft: 1, verticalAlign: "text-bottom", animation: "blink 1s step-end infinite" }} />}
                          </div>
                          {/* Data table — shown as soon as rows arrive, before answer streams */}
                          {item.rows && item.rows.length > 0 && (
                            <div style={{ marginTop: 10, overflowX: "auto" }}>
                              <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: 4 }}>{item.row_count} row{item.row_count !== 1 ? "s" : ""} returned</div>
                              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                                <thead>
                                  <tr>
                                    {item.columns.map(col => (
                                      <th key={col} style={{ padding: "6px 10px", textAlign: "left", borderBottom: `1px solid ${COLORS.border}`, color: COLORS.textMuted, fontWeight: 600, whiteSpace: "nowrap" }}>{col}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {item.rows.map((row, ri) => (
                                    <tr key={ri}>
                                      {item.columns.map(col => (
                                        <td key={col} style={{ padding: "5px 10px", borderBottom: `1px solid ${COLORS.border}08`, color: COLORS.text, whiteSpace: "nowrap" }}>
                                          {row[col] != null ? String(row[col]) : "—"}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                          {/* SQL query (collapsible) */}
                          {item.sql && (
                            <details style={{ marginTop: 8 }}>
                              <summary style={{ fontSize: 10, color: COLORS.textDim, cursor: "pointer" }}>View SQL query</summary>
                              <pre style={{
                                marginTop: 4, padding: 10, borderRadius: 8,
                                background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                                fontSize: 11, color: COLORS.textMuted, overflowX: "auto", whiteSpace: "pre-wrap",
                              }}>{item.sql}</pre>
                            </details>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{ display: "flex", gap: 8 }}>
            <input
              type="text" value={aiQuestion}
              onChange={e => setAiQuestion(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") handleAskAI(); }}
              placeholder="e.g. What are the top complaints this week? Which agent has the highest escalation rate?"
              style={{
                flex: 1, padding: "10px 14px", borderRadius: 10,
                border: `1px solid ${COLORS.border}`, background: COLORS.surface,
                color: COLORS.text, fontSize: 13, outline: "none",
              }}
              onFocus={e => e.target.style.borderColor = COLORS.accent}
              onBlur={e => e.target.style.borderColor = COLORS.border}
            />
            <Button onClick={handleAskAI} disabled={aiAsking || !aiQuestion.trim()}
              style={{
                background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.purple})`,
                border: "none", color: "#fff", padding: "10px 20px", borderRadius: 10,
                opacity: aiAsking || !aiQuestion.trim() ? 0.5 : 1,
              }}>
              {aiAsking ? "..." : "Ask"}
            </Button>
          </div>

          {/* Quick suggestions */}
          {aiHistory.length === 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
              {[
                "What are the top customer intents this week?",
                "Which agent has the highest escalation rate?",
                "Show conversation volume by channel for the last 30 days",
                "What is the average CSAT score by agent?",
                "How many conversations were resolved vs escalated today?",
                "What are the busiest hours for incoming messages?",
              ].map(q => (
                <button key={q} onClick={() => { setAiQuestion(q); }}
                  style={{
                    padding: "5px 10px", borderRadius: 8, border: `1px solid ${COLORS.border}`,
                    background: COLORS.surface, color: COLORS.textMuted, fontSize: 11,
                    cursor: "pointer", transition: "all 0.15s",
                  }}
                  onMouseEnter={e => { e.target.style.borderColor = COLORS.accent; e.target.style.color = COLORS.accent; }}
                  onMouseLeave={e => { e.target.style.borderColor = COLORS.border; e.target.style.color = COLORS.textMuted; }}
                >{q}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* A/B Tests */}
      <ABTestPanel />

      {/* Elasticsearch Explorer */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: bp.isMobile ? 18 : 20, fontWeight: 700, color: COLORS.text, letterSpacing: "-0.02em" }}>Elasticsearch Explorer</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>Search across messages, files, and events indexed in Elasticsearch</div>
        </div>

        {/* Index stats cards */}
        {esStats && (
          <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1fr" : "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
            {[
              { key: "messages", label: "Messages", icon: "\uD83D\uDCAC", color: COLORS.accentText },
              { key: "files", label: "Files", icon: "\uD83D\uDCC1", color: COLORS.cyan },
              { key: "events", label: "Events", icon: "\u26A1", color: COLORS.orange },
            ].map(idx => (
              <div key={idx.key} onClick={() => { setEsIndex(idx.key); setEsResults(null); }}
                style={{
                  padding: "16px 18px", borderRadius: 12, cursor: "pointer",
                  background: esIndex === idx.key ? `${idx.color}10` : COLORS.surface,
                  border: `1px solid ${esIndex === idx.key ? `${idx.color}40` : COLORS.border}`,
                  transition: "all 0.15s",
                }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{idx.icon} {idx.label}</span>
                  <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: "monospace" }}>{esStats[idx.key]?.index_name}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontSize: 22, fontWeight: 700, color: idx.color }}>{(esStats[idx.key]?.doc_count || 0).toLocaleString()}</span>
                  <span
                    style={{ fontSize: 11, color: COLORS.textMuted }}
                    title={esStats[idx.key]?.size_scope === "index"
                      ? "Index size on disk — shared across all tenants (Elasticsearch does not expose per-tenant storage)"
                      : "Storage size"}>
                    {formatBytes(esStats[idx.key]?.size_bytes)}
                    {esStats[idx.key]?.size_scope === "index" && (
                      <span style={{ marginLeft: 4, opacity: 0.6 }}>(index)</span>
                    )}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Search bar + index tabs */}
        <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(), padding: "16px 20px", marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
            {[
              { key: "messages", label: "Messages" },
              { key: "files", label: "Files" },
              { key: "events", label: "Events" },
              { key: "all", label: "All Indices" },
            ].map(tab => (
              <Button key={tab.key} variant={esIndex === tab.key ? "primary" : "secondary"} size="sm"
                onClick={() => { setEsIndex(tab.key); setEsResults(null); }}
                style={esIndex === tab.key
                  ? { background: COLORS.accentSoft, color: COLORS.accentText, border: `1px solid ${COLORS.accent}`, padding: "6px 14px", fontSize: 11 }
                  : { padding: "6px 14px", fontSize: 11 }
                }>
                {tab.label}
              </Button>
            ))}
          </div>
          {/* Date range filters */}
          <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 600, marginRight: 2 }}>Period:</span>
            {[
              { key: "24h", label: "24h" },
              { key: "7d", label: "7 days" },
              { key: "30d", label: "30 days" },
              { key: "90d", label: "90 days" },
            ].map(p => (
              <button key={p.key} onClick={() => applyDatePreset(datePreset === p.key ? "" : p.key)}
                style={{
                  padding: "4px 12px", borderRadius: 20, fontSize: 10, fontWeight: 600, cursor: "pointer",
                  border: `1px solid ${datePreset === p.key ? COLORS.accent + "60" : COLORS.border}`,
                  background: datePreset === p.key ? COLORS.accentSoft : COLORS.bg,
                  color: datePreset === p.key ? COLORS.accent : COLORS.textMuted,
                  transition: "all 0.15s",
                }}>{p.label}</button>
            ))}
            <span style={{ fontSize: 11, color: COLORS.textDim, margin: "0 4px" }}>or</span>
            <input type="date" value={dateFrom}
              onChange={e => { setDateFrom(e.target.value); setDatePreset(""); }}
              style={{
                padding: "4px 8px", borderRadius: 8, fontSize: 11,
                border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text,
                colorScheme: "dark",
              }}
            />
            <span style={{ fontSize: 11, color: COLORS.textDim }}>to</span>
            <input type="date" value={dateTo}
              onChange={e => { setDateTo(e.target.value); setDatePreset(""); }}
              style={{
                padding: "4px 8px", borderRadius: 8, fontSize: 11,
                border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text,
                colorScheme: "dark",
              }}
            />
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(""); setDateTo(""); setDatePreset(""); }}
                style={{
                  padding: "4px 8px", borderRadius: 20, fontSize: 10, cursor: "pointer",
                  border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textDim,
                }}>Clear</button>
            )}
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <Input
              type="text" value={esQuery} onChange={e => setEsQuery(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleEsSearch()}
              placeholder={`Search ${esIndex === "all" ? "all indices" : esIndex}...`}
              style={{ flex: 1, padding: "10px 14px", borderRadius: 10, fontSize: 13 }}
            />
            <Button onClick={() => handleEsSearch()} loading={esSearching}
              style={{
                padding: "10px 20px", borderRadius: 10,
                background: `linear-gradient(135deg, ${COLORS.accent}, #6366F1)`,
                boxShadow: `0 4px 12px ${COLORS.accentGlow}`, fontSize: 13,
              }}>
              Search
            </Button>
            {esIndex === "messages" && (
              <Button onClick={handleAiSummarize} loading={aiSummarizing}
                style={{
                  padding: "10px 20px", borderRadius: 10,
                  background: `linear-gradient(135deg, #10B981, #059669)`,
                  boxShadow: "0 4px 12px rgba(16,185,129,0.3)", fontSize: 13,
                }}>
                Summarize with AI
              </Button>
            )}
          </div>

          {/* Quick filters */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
            {quickFilters.map(f => (
              <button key={f.label} onClick={() => { setEsIndex(f.index); setEsQuery(f.query); handleEsSearch(f.query, f.index); }}
                style={{
                  padding: "4px 12px", borderRadius: 20, fontSize: 10, fontWeight: 500, cursor: "pointer",
                  border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textMuted,
                  transition: "all 0.15s",
                }}>{f.label}</button>
            ))}
          </div>
        </div>

        {/* AI Summary */}
        {aiSummarizing && (
          <div style={{ background: COLORS.surface, border: `1px solid #10B98140`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(), padding: "20px 24px", marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: COLORS.textMuted, animation: "pulse 1.5s ease-in-out infinite" }}>Generating AI summary...</div>
          </div>
        )}
        {aiSummary && !aiSummarizing && (
          <div style={{ background: COLORS.surface, border: `1px solid #10B98140`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(), padding: "20px 24px", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 10, fontWeight: 600, padding: "3px 10px", borderRadius: 6, background: "#10B98120", color: "#10B981", textTransform: "uppercase" }}>AI Summary</span>
                <span style={{ fontSize: 11, color: COLORS.textDim }}>{aiSummary.message_count} messages analyzed</span>
                {(aiSummary.date_from || aiSummary.date_to) && (
                  <span style={{ fontSize: 10, padding: "3px 10px", borderRadius: 6, background: `${COLORS.accent}10`, color: COLORS.accentText }}>
                    {aiSummary.date_from && aiSummary.date_to ? `${aiSummary.date_from} — ${aiSummary.date_to}` : aiSummary.date_from ? `From ${aiSummary.date_from}` : `Until ${aiSummary.date_to}`}
                  </span>
                )}
              </div>
              <button onClick={() => setAiSummary(null)} style={{ background: "none", border: "none", color: COLORS.textDim, cursor: "pointer", fontSize: 16 }}>&times;</button>
            </div>
            <div style={{ fontSize: 13, color: aiSummary.error ? COLORS.pink : COLORS.text, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{aiSummary.summary}</div>
          </div>
        )}

        {/* Results */}
        <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(), padding: "20px 24px" }}>
          {/* Results header */}
          {esResults && !esResults.error && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <span style={{
                fontSize: 10, fontWeight: 600, padding: "3px 10px", borderRadius: 6,
                background: `${COLORS.accent}15`, color: COLORS.accentText, textTransform: "uppercase",
              }}>{esResults.index === "all" ? "All Indices" : esResults.index}</span>
              <span style={{ fontSize: 12, color: COLORS.text, fontWeight: 600 }}>
                {(esResults.index === "all"
                  ? (esResults.files?.total || 0) + (esResults.events?.total || 0) + (esResults.messages?.total || 0)
                  : esResults.total || 0
                ).toLocaleString()} results
              </span>
              <span style={{ fontSize: 11, color: COLORS.textDim }}>{esResults.took_ms || 0}ms</span>
            </div>
          )}

          {esResults?.error && (
            <div style={{
              padding: "12px 14px", borderRadius: 10, marginBottom: 14,
              background: `${COLORS.pink}10`, border: `1px solid ${COLORS.pink}25`, color: COLORS.pink, fontSize: 12,
            }}>{esResults.error}</div>
          )}

          {/* Empty state */}
          {!esResults && !esSearching && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px" }}>
              <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>&#x1F50D;</div>
              <div style={{ fontSize: 13, color: COLORS.textDim, textAlign: "center" }}>Enter a search query or click a quick filter to explore your data</div>
            </div>
          )}

          {esSearching && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px" }}>
              <div style={{ fontSize: 13, color: COLORS.textMuted, animation: "pulse 1.5s ease-in-out infinite" }}>Searching Elasticsearch...</div>
            </div>
          )}

          {/* "all" index results */}
          {esResults && esResults.index === "all" && !esResults.error && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {[
                { key: "messages", label: "Messages", icon: "\uD83D\uDCAC", color: COLORS.accentText },
                { key: "files", label: "Files", icon: "\uD83D\uDCC1", color: COLORS.cyan },
                { key: "events", label: "Events", icon: "\u26A1", color: COLORS.orange },
              ].map(section => {
                const sData = esResults[section.key];
                if (!sData || !sData.hits?.length) return null;
                return (
                  <div key={section.key}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: section.color, marginBottom: 10 }}>
                      {section.icon} {section.label} ({sData.total})
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {sData.hits.map((h, i) => (
                        <div key={i} style={{
                          padding: "10px 14px", borderRadius: 10, background: COLORS.surface,
                          border: `1px solid ${COLORS.border}`, fontSize: 12, color: COLORS.text,
                        }}>
                          {section.key === "messages" && (
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: h.role === "user" ? `${COLORS.accent}15` : `${COLORS.green}15`, color: h.role === "user" ? COLORS.accent : COLORS.green }}>{h.role}</span>
                              {h.channel && <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: `${COLORS.orange}10`, color: COLORS.orange }}>{h.channel}</span>}
                              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.content}</span>
                              <OpenInInspectorLink conversationId={h.conversation_id} navigate={_navigate} />
                            </div>
                          )}
                          {section.key === "files" && (
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: `${COLORS.cyan}15`, color: COLORS.cyan }}>{h.content_type?.split("/")[1] || "file"}</span>
                              <span style={{ fontWeight: 500 }}>{h.filename}</span>
                              <span style={{ fontSize: 10, color: COLORS.textDim, marginLeft: "auto" }}>{formatBytes(h.size_bytes)}</span>
                            </div>
                          )}
                          {section.key === "events" && (
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: `${COLORS.orange}15`, color: COLORS.orange }}>{h.event_type}</span>
                              <span style={{ fontSize: 10, color: COLORS.textDim }}>{h.timestamp ? new Date(h.timestamp).toLocaleString() : ""}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {!esResults.messages?.hits?.length && !esResults.files?.hits?.length && !esResults.events?.hits?.length && (
                <div style={{ padding: "40px 20px", textAlign: "center", color: COLORS.textDim, fontSize: 13 }}>No results found across any index</div>
              )}
            </div>
          )}

          {/* Single index results */}
          {esResults && esResults.index !== "all" && !esResults.error && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {esResults.hits?.length === 0 && (
                <div style={{ padding: "40px 20px", textAlign: "center", color: COLORS.textDim, fontSize: 13 }}>No results found</div>
              )}
              {esResults.hits?.map((h, i) => {
                const cardId = h.message_id || h.file_id || h.event_id || `r-${i}`;
                const isExpanded = esExpandedId === cardId;
                return (
                  <div key={cardId} onClick={() => setEsExpandedId(isExpanded ? null : cardId)}
                    style={{
                      padding: "14px 16px", borderRadius: 12, background: COLORS.surface, cursor: "pointer",
                      border: `1px solid ${isExpanded ? COLORS.accent + "40" : COLORS.border}`,
                      transition: "border-color 0.15s",
                    }}>
                    {/* Message card */}
                    {esResults.index === "messages" && (<>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 6,
                          background: h.role === "user" ? `${COLORS.accent}15` : `${COLORS.green}15`,
                          color: h.role === "user" ? COLORS.accent : COLORS.green,
                        }}>{h.role}</span>
                        {h.channel && <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 6, background: `${COLORS.orange}10`, color: COLORS.orange }}>{h.channel}</span>}
                        {h.intent && <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 6, background: `${COLORS.cyan}10`, color: COLORS.cyan }}>{h.intent}</span>}
                        <span style={{ fontSize: 10, color: COLORS.textDim, marginLeft: "auto" }}>{h.created_at ? new Date(h.created_at).toLocaleString() : ""}</span>
                      </div>
                      <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.6, maxHeight: isExpanded ? "none" : 60, overflow: "hidden" }}>{h.content}</div>
                      {isExpanded && (
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10, paddingTop: 10, borderTop: `1px solid ${COLORS.border}` }}>
                          {h.model_used && <span style={{ fontSize: 10, color: COLORS.textDim, background: `${COLORS.accent}08`, padding: "3px 8px", borderRadius: 4 }}>model: {h.model_used}</span>}
                          {h.latency_ms != null && <span style={{ fontSize: 10, color: COLORS.textDim, background: `${COLORS.green}08`, padding: "3px 8px", borderRadius: 4 }}>latency: {h.latency_ms}ms</span>}
                          {h.token_count != null && <span style={{ fontSize: 10, color: COLORS.textDim, background: `${COLORS.orange}08`, padding: "3px 8px", borderRadius: 4 }}>tokens: {h.token_count}</span>}
                          {h.status && <span style={{ fontSize: 10, color: COLORS.textDim, background: `${COLORS.cyan}08`, padding: "3px 8px", borderRadius: 4 }}>status: {h.status}</span>}
                          {h.agent_id && <span style={{ fontSize: 10, color: COLORS.textDim, background: `${COLORS.pink}08`, padding: "3px 8px", borderRadius: 4 }}>agent: {h.agent_id?.slice(0, 8)}</span>}
                          {h.conversation_id && <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: "monospace" }}>conv: {h.conversation_id?.slice(0, 8)}</span>}
                          <OpenInInspectorLink conversationId={h.conversation_id} navigate={_navigate} />
                        </div>
                      )}
                    </>)}

                    {/* File card */}
                    {esResults.index === "files" && (<>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 6, background: `${COLORS.cyan}15`, color: COLORS.cyan }}>
                          {h.content_type?.split("/")[1] || "file"}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.filename}</span>
                        <span style={{ fontSize: 11, color: COLORS.textDim }}>{formatBytes(h.size_bytes)}</span>
                      </div>
                      <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: isExpanded ? 10 : 0 }}>
                        Uploaded: {h.uploaded_at ? new Date(h.uploaded_at).toLocaleString() : "N/A"}
                        {h.user_id && <span> &middot; User: {h.user_id.slice(0, 8)}</span>}
                      </div>
                      {isExpanded && h.text_content && (
                        <div style={{
                          marginTop: 8, padding: "10px 12px", borderRadius: 8, background: COLORS.surface,
                          border: `1px solid ${COLORS.border}`, fontSize: 11, color: COLORS.text,
                          lineHeight: 1.6, maxHeight: 200, overflow: "auto", fontFamily: "monospace", whiteSpace: "pre-wrap",
                        }}>{h.text_content.slice(0, 2000)}</div>
                      )}
                    </>)}

                    {/* Event card */}
                    {esResults.index === "events" && (<>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 6, background: `${COLORS.orange}15`, color: COLORS.orange }}>{h.event_type}</span>
                        <span style={{ fontSize: 10, color: COLORS.textDim, marginLeft: "auto" }}>{h.timestamp ? new Date(h.timestamp).toLocaleString() : ""}</span>
                      </div>
                      {h.tenant_id && <div style={{ fontSize: 10, color: COLORS.textDim }}>Tenant: {h.tenant_id}</div>}
                      {isExpanded && h.payload && (
                        <div style={{
                          marginTop: 8, padding: "10px 12px", borderRadius: 8, background: COLORS.surface,
                          border: `1px solid ${COLORS.border}`, fontSize: 11, color: COLORS.text,
                          lineHeight: 1.5, maxHeight: 250, overflow: "auto", fontFamily: "monospace", whiteSpace: "pre-wrap",
                        }}>{JSON.stringify(h.payload, null, 2)}</div>
                      )}
                    </>)}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsView;
export { MiniBarChart };
